import { SUBSCRIPTION_STATUS_LABELS } from "@iagenix/shared";
import { useQuery } from "@tanstack/react-query";
import { Link, createFileRoute } from "@tanstack/react-router";
import { useDeferredValue, useState } from "react";
import { PageHeader } from "@/components/page-header";
import { EmptyState, ErrorState, LoadingState } from "@/components/states";
import { StatusBadge } from "@/components/billing/status-badge";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { billingQueries, brl, shortDate } from "@/lib/billing-api";

export const Route = createFileRoute("/admin/workspaces")({
  component: AdminWorkspacesPage,
});

const selectClass = "h-9 rounded-md border border-input bg-transparent px-2 text-sm";

function AdminWorkspacesPage() {
  const [q, setQ] = useState("");
  const [plan, setPlan] = useState("");
  const [status, setStatus] = useState("");
  const deferredQ = useDeferredValue(q);
  const list = useQuery(billingQueries.adminWorkspaces({ q: deferredQ, plan, status }));
  const plans = useQuery(billingQueries.adminPlans());
  return (
    <>
      <PageHeader title="Workspaces" description="Todos os clientes do SaaS: plano, status, MRR e uso." />
      <div className="mb-4 flex flex-col gap-2 sm:flex-row">
        <Input placeholder="Buscar por nome, e-mail do dono ou id" value={q} onChange={(e) => setQ(e.target.value)} className="sm:max-w-xs" aria-label="Buscar" />
        <select className={selectClass} value={plan} onChange={(e) => setPlan(e.target.value)} aria-label="Filtrar por plano">
          <option value="">Todos os planos</option>
          {plans.data?.map((p) => (
            <option key={p.code} value={p.code}>
              {p.name}
            </option>
          ))}
        </select>
        <select className={selectClass} value={status} onChange={(e) => setStatus(e.target.value)} aria-label="Filtrar por status">
          <option value="">Todos os status</option>
          {Object.entries(SUBSCRIPTION_STATUS_LABELS).map(([k, v]) => (
            <option key={k} value={k}>
              {v}
            </option>
          ))}
          <option value="modo_leitura">Em modo leitura</option>
          <option value="suspenso">Suspensos</option>
        </select>
      </div>
      {list.isPending ? (
        <LoadingState rows={6} />
      ) : list.isError ? (
        <ErrorState error={list.error} onRetry={() => void list.refetch()} />
      ) : list.data.length === 0 ? (
        <EmptyState title="Nenhum workspace encontrado" description="Mude a busca ou os filtros." />
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Workspace</TableHead>
              <TableHead>Plano</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">MRR</TableHead>
              <TableHead className="text-right">Membros</TableHead>
              <TableHead className="text-right">Contas</TableHead>
              <TableHead>Criado</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {list.data.map((w) => (
              <TableRow key={w.id}>
                <TableCell>
                  <Link to="/admin/workspaces/$id" params={{ id: w.id }} className="font-medium hover:underline">
                    {w.name}
                  </Link>
                  <p className="text-xs text-muted-foreground">{w.ownerEmail ?? "sem dono"}</p>
                </TableCell>
                <TableCell>{w.planName}</TableCell>
                <TableCell>
                  <StatusBadge row={w} />
                  {w.status === "trialing" && w.trialEndsAt ? <p className="text-xs text-muted-foreground">até {shortDate(w.trialEndsAt)}</p> : null}
                </TableCell>
                <TableCell className="text-right tabular-nums">{brl(w.mrrCents)}</TableCell>
                <TableCell className="text-right tabular-nums">{w.members}</TableCell>
                <TableCell className="text-right tabular-nums">{w.adAccounts}</TableCell>
                <TableCell>{shortDate(w.createdAt)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </>
  );
}
